'use client';
/* eslint-disable @next/next/no-img-element */
import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import LazyLoad from 'react-lazyload';

import { DeferredMount } from '@benzinga/hooks';
import { JsonLd } from '@benzinga/seo';
import { buildQuoteCorporationSchema } from './quoteCorporationSchema';
import { ErrorBoundary, SectionTitle, Spinner } from '@benzinga/core-ui';
import dynamic from 'next/dynamic';
import type { StockSymbol } from '@benzinga/session';
import type { ShortInterest } from '@benzinga/quotes-manager';
import type { AnalystRatingsSummary } from '@benzinga/calendar-manager';
import type { Financials } from '@benzinga/securities-manager';
import type { StoryObject } from '@benzinga/basic-news-manager';
import type { SimpleNewsQueryAndOptions } from '@benzinga/internal-news-manager';
import type { QuoteVideoRecord } from '@benzinga/videos-manager';
import type {
  BaseQuoteProfile,
  BaseQuotePageProps,
  OverviewValues,
  QuoteFund,
} from '../../../../src/utils/quote/types';
import type { DividendSummary, EarningsSummary, StockSplitsSummary } from '../../../../src/utils/quote/summaries';
import type { QuoteTechnicals } from '../../../../src/entities/quoteEntity';
import type { PressContent } from '../../../../src/entities/newsEntity';
import type { InvestorsResponse } from '@benzinga/investors-manager';

import FinancialsSection from '../../../../src/components/Quote/FinancialsSection';
import { MetricsSection } from '../../../../src/components/Quote/MetricsSection';
import OverviewRow from '../../../../src/components/Quote/OverviewRow';
import QuoteNewsSection from '../../../../src/components/Quote/QuoteNewsSection';
import AnalystRatingsCharts from '../../../../src/components/Quote/AnalystRatingsCharts';
import DrilldownFooter from '../../../../src/components/Quote/DrilldownFooter';
import { QuoteFooter } from '../../../../src/components/Quote/QuoteFooter';
import { MetricsSidebar } from '../../../../src/components/Quote/MetricsSidebar';
import { QuotePageSidebar } from '../../../../src/components/Quote/QuotePageSidebar';
import { PrivateCompanyOverview } from '../../../../src/components/Quote/PrivateCompanyOverview';
import type { ChartConfig, SupportedChartTypes, TimeFrameKey } from '@benzinga/chartiq-core/lightweight';

const ChartIQLightweightChart = dynamic(
  () => import('@benzinga/chartiq-lightweight-chart').then(module => ({ default: module.ChartIQLightweightChart })),
  { ssr: false, loading: () => <Spinner /> },
);

const CompareSection = dynamic(() => import('../../../../src/components/Quote/CompareSection'), {
  ssr: false,
  loading: () => <div className="min-h-[375px]" />,
});

const OverviewSectionHeader = React.lazy(() =>
  import('../../../../src/components/Quote/OverviewSectionHeader').then(module => ({
    default: module.OverviewSectionHeader,
  })),
);

const CryptoOverviewHeader = React.lazy(() =>
  import('../../../../src/components/Quote/CryptoOverviewHeader').then(module => ({
    default: module.CryptoOverviewHeader,
  })),
);

const ETFOverviewHeader = React.lazy(() =>
  import('../../../../src/components/Quote/ETFOverviewHeader').then(module => ({ default: module.ETFOverviewHeader })),
);

const ProfileFAQ = React.lazy(() =>
  import('../../../../src/components/Quote/FAQ/ProfileFAQ').then(module => ({ default: module.ProfileFAQ })),
);

const TaboolaPlacement = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.TaboolaPlacement })),
);

const QuoteVideoSection = dynamic(
  () => import('@benzinga/youtube-video-embed-ui').then(module => ({ default: module.QuoteVideoSection })),
  { loading: () => null },
);

const FAQSection: React.FC<{
  title: string;
  profile: BaseQuoteProfile;
  splitsSummary: StockSplitsSummary | null;
  dividendSummary: DividendSummary | null;
  earningsSummary: EarningsSummary | null;
  ratingsSummary: AnalystRatingsSummary | null;
  fundamentals?: Financials | null;
}> = ({ profile, title, splitsSummary, dividendSummary, earningsSummary, ratingsSummary, fundamentals }) => (
  <>
    <SectionTitle level={2} size="2xl">
      {title}
    </SectionTitle>
    <ProfileFAQ
      dividendSummary={dividendSummary}
      earningsSummary={earningsSummary}
      fundamentals={fundamentals}
      profile={profile}
      ratingsSummary={ratingsSummary}
      searchValue={profile.symbol}
      splitsSummary={splitsSummary}
    />
  </>
);

interface OverviewContentProps extends BaseQuotePageProps {
  splitsSummary?: StockSplitsSummary | null;
  dividendSummary?: DividendSummary | null;
  earningsSummary?: EarningsSummary | null;
  fundamentals: Financials | null;
  fundamentalsAnnual: Financials | null;
  ratingsSummary: AnalystRatingsSummary | null;
  technicals: QuoteTechnicals | null;
  shortInterest: ShortInterest[] | null;
  etfFund: QuoteFund | null;
  overviewValues: OverviewValues | null;
  pressRelease: PressContent | null;
  recentNews: StoryObject[];
  recentNewsQuery: SimpleNewsQueryAndOptions;
  investors?: InvestorsResponse | null;
  tickerVideos: QuoteVideoRecord[];
}

const OverviewContent = ({
  etfFund,
  fundamentals,
  metaProps,
  profile,
  ratingsSummary,
  technicals,
  recentNews,
  recentNewsQuery,
  pressRelease,
  splitsSummary,
  dividendSummary,
  earningsSummary,
  shortInterest,
  overviewValues,
  fundamentalsAnnual,
  investors = null,
  tickerVideos,
}: OverviewContentProps) => {
  const { t } = useTranslation(['quote', 'common']);

  if (profile.isPrivate) {
    return (
      <PrivateCompanyOverview
        activeTab="overview"
        investors={investors}
        metaProps={metaProps}
        profile={profile}
        symbol={profile.symbol}
      />
    );
  }

  const compareData = {
    de: fundamentals?.operationRatios?.[0]?.totalDebtEquityRatio ?? 0,
    eps: fundamentals?.earningReports?.[0]?.basicEps ?? 0,
    pb: fundamentals?.valuationRatios?.[0]?.pbRatio ?? 0,
    pe: fundamentals?.valuationRatios?.[0]?.peRatio ?? 0,
    roe: fundamentals?.operationRatios?.[0]?.roe ?? 0,
    rsi: technicals?.rsi ?? 0,
  };

  const peersSymbols = profile.peerSymbols.filter(peerSymbol => peerSymbol !== profile.symbol);

  const equityCompanyName = profile?.richQuoteData?.issuerName || profile?.richQuoteData?.name || profile?.symbol || '';
  const cryptoCompanyName = profile?.cryptoData?.name || profile?.richQuoteData?.description || profile?.symbol || '';

  const dotComLightWeightConfig = {
    type: 'mountain' as SupportedChartTypes,
    candle: {
      timeframe: '2d' as TimeFrameKey,
    },
    chartConfig: {
      periodOptions: {
        content: [
          {
            label: '1 D',
            tap: 'Layout.setPeriodicity',
            type: 'item',
            value: [1, 1, 'day'],
          },
          {
            label: '1 W',
            tap: 'Layout.setPeriodicity',
            type: 'item',
            value: [1, 1, 'week'],
          },
          {
            label: '1 Mo',
            tap: 'Layout.setPeriodicity',
            type: 'item',
            value: [1, 1, 'month'],
          },
          {
            menuPersist: true,
            type: 'separator',
          },
          {
            label: '1 Min',
            tap: 'Layout.setPeriodicity',
            type: 'item',
            value: [1, 1, 'minute'],
          },
          {
            label: '5 Min',
            tap: 'Layout.setPeriodicity',
            type: 'item',
            value: [1, 5, 'minute'],
          },
          {
            label: '10 Min',
            tap: 'Layout.setPeriodicity',
            type: 'item',
            value: [1, 10, 'minute'],
          },
          {
            label: '15 Min',
            tap: 'Layout.setPeriodicity',
            type: 'item',
            value: [3, 5, 'minute'],
          },
          {
            label: '30 Min',
            tap: 'Layout.setPeriodicity',
            type: 'item',
            value: [1, 30, 'minute'],
          },
          {
            label: '1 Hour',
            tap: 'Layout.setPeriodicity',
            type: 'item',
            value: [2, 30, 'minute'],
          },
          {
            label: '4 Hour',
            tap: 'Layout.setPeriodicity',
            type: 'item',
            value: [8, 30, 'minute'],
          },
        ],
      },
    } as ChartConfig,
    studies: ['vol undr'],
  };

  return (
    <>
      {profile?.tickerDetails && (
        <JsonLd
          data={buildQuoteCorporationSchema(
            profile.symbol,
            profile?.richQuoteData,
            profile.tickerDetails,
            metaProps?.canonical ?? '',
            fundamentals,
            profile?.logoUrl,
          )}
          id="corporation-schema"
        />
      )}
      <div className="main-div w-full flex gap-4 px-4">
        <div className="w-full flex flex-col gap-8 md:gap-4 p-0">
          {profile.isETF && etfFund && (
            <ETFOverviewHeader etfFund={etfFund} profile={profile} rankingData={profile.rankingData} />
          )}
          {profile.isCrypto && <CryptoOverviewHeader profile={profile} rankingData={profile.rankingData} />}
          <div className="top-area-and-sidebar-container w-full flex mx-auto max-w-[1400px] gap-4">
            <div className="w-full lg:w-8/12 flex flex-col gap-8 md:gap-4">
              <div className="">
                <div className="w-full flex flex-col gap-8">
                  <div className="w-full flex flex-col lg:flex-row gap-8 md:gap-4">
                    <div className="w-full flex flex-col">
                      <div className="flex flex-col gap-8 md:gap-4">
                        <div
                          className={classNames(
                            'card flex-grow flex flex-col h-[400px] !p-0 bg-white !p-0 bg-white overflow-hidden',
                            {
                              'md:h-[500px]': !profile.isETF,
                              'md:h-[520px]': profile.isETF,
                            },
                          )}
                        >
                          <ErrorBoundary name="quote-chartiq-lightweight">
                            <React.Suspense
                              fallback={
                                <div className="flex h-full w-full items-center justify-center">
                                  <Spinner />
                                </div>
                              }
                            >
                              <ChartIQLightweightChart
                                currentTheme="light"
                                variant={{
                                  type: 'dotcom',
                                  config: dotComLightWeightConfig,
                                }}
                                symbol={profile.symbol as StockSymbol}
                              />
                            </React.Suspense>
                          </ErrorBoundary>
                        </div>
                        <div className="metrics-mobile w-full">
                          <MetricsSidebar
                            etfFund={etfFund ?? null}
                            fundamentals={fundamentals ?? null}
                            isCrypto={profile.isCrypto}
                            isETF={profile.isETF}
                            profile={profile}
                            shortInterest={shortInterest ?? null}
                            technicals={technicals ?? { rsi: 0 }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full">
                    <ErrorBoundary name="quote-news-section">
                      {profile.isCrypto ? (
                        <QuoteNewsSection
                          news={recentNews || []}
                          query={recentNewsQuery || {}}
                          companyName={cryptoCompanyName}
                          variant="crypto"
                        />
                      ) : (
                        <div className="flex flex-col">
                          <QuoteNewsSection
                            belowNewsSection={
                              profile.isETF ? (
                                <FAQSection
                                  dividendSummary={dividendSummary ?? null}
                                  earningsSummary={earningsSummary ?? null}
                                  fundamentals={fundamentals}
                                  profile={profile}
                                  ratingsSummary={ratingsSummary ?? null}
                                  splitsSummary={splitsSummary ?? null}
                                  title={t('Quote.FAQ.faq')}
                                />
                              ) : null
                            }
                            news={recentNews || []}
                            query={recentNewsQuery || {}}
                            pressRelease={pressRelease || null}
                            companyName={equityCompanyName}
                          />
                        </div>
                      )}
                    </ErrorBoundary>
                  </div>
                </div>
              </div>

              <QuoteVideoSection key={profile.symbol} records={tickerVideos} symbol={profile.symbol} />

              {!profile.isETF && !profile.isCrypto && (
                <div className="mx-auto max-w-[1400px] w-full">
                  {overviewValues?.financials && (
                    <OverviewSectionHeader title={t('Quote.Financials.title')} values={overviewValues.financials} />
                  )}
                  <div className="flex flex-col gap-8 md:gap-4 lg:flex-row">
                    <div className="w-full flex flex-col gap-8 md:gap-4">
                      <FinancialsSection symbol={profile.symbol} />
                      <div className="flex flex-col gap-8 md:gap-4">
                        <MetricsSection
                          section="profitability"
                          fundamentals={fundamentalsAnnual ?? fundamentals ?? null}
                        />
                        <MetricsSection section="cashFlows" fundamentals={fundamentalsAnnual ?? fundamentals ?? null} />
                      </div>
                      <AnalystRatingsCharts
                        averageRating={ratingsSummary?.consensusRatingValue ?? null}
                        ratings={ratingsSummary?.uniqueRatingsFromLastThreeYears}
                        ratingsByMonthWithCounts={overviewValues?.ratingsByMonthWithCounts || []}
                      />
                      <div className="flex flex-col gap-8 md:gap-4">
                        <MetricsSection section="valuation" fundamentals={fundamentalsAnnual ?? fundamentals ?? null} />
                        <MetricsSection
                          section="debt"
                          fundamentals={fundamentals ?? null}
                          fundamentalsAnnual={fundamentalsAnnual ?? null}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <QuotePageSidebar
              isCrypto={profile.isCrypto}
              fundamentals={fundamentals ?? null}
              technicals={technicals ?? { rsi: 0 }}
              shortInterest={shortInterest ?? null}
              isETF={profile.isETF}
              profile={profile}
              symbol={profile.symbol}
              etfFund={etfFund ?? null}
            />
          </div>
          {!profile.isETF && !profile.isCrypto && (
            <OverviewRow
              bullSayBearSay={overviewValues?.bullSayBearSay || null}
              financials={overviewValues?.financials || { down: 0, up: 0 }}
              rankingData={profile.rankingData}
              symbol={profile.symbol}
              technicals={overviewValues?.technicals || { down: 0, up: 0 }}
            />
          )}
          {!profile.isETF && !profile.isCrypto && (
            <DeferredMount fallback={<div className="min-h-[375px]" />} rootMargin="200px" warmAfterMs={0}>
              <CompareSection
                compareData={compareData}
                currentSymbol={profile.symbol}
                initialSymbols={peersSymbols[0] ? [profile.symbol, peersSymbols[0]] : [profile.symbol]}
              />
            </DeferredMount>
          )}
          {!profile.isCrypto && (
            <DrilldownFooter
              isETF={profile.isETF}
              leftSection={
                !profile.isETF ? (
                  <FAQSection
                    dividendSummary={dividendSummary ?? null}
                    earningsSummary={earningsSummary ?? null}
                    fundamentals={fundamentals}
                    profile={profile}
                    ratingsSummary={ratingsSummary ?? null}
                    splitsSummary={splitsSummary ?? null}
                    title={t('Quote.FAQ.faq')}
                  />
                ) : null
              }
              peers={profile.peers}
              peerSymbols={profile.peerSymbols}
              symbol={profile.symbol}
              type={profile?.schedule?.type || 'REGULAR'}
            />
          )}
          {!profile.isCrypto && <QuoteFooter activeTab="overview" showBrowseLink={false} />}
          <div className="mx-auto max-w-[850px]">
            <LazyLoad offset={100} once>
              <div className="wrapper w-full mb-5 overflow-hidden">
                <div className="mx-auto">
                  <React.Suspense fallback={<div />}>
                    <TaboolaPlacement
                      container="taboola-feed-below-quotes"
                      mode="alternating-thumbnails-a"
                      placement="Feed Below Quotes"
                      settings={{
                        other: 'auto',
                      }}
                      url=""
                      vizSensor={false}
                    />
                  </React.Suspense>
                </div>
              </div>
            </LazyLoad>
          </div>
        </div>
      </div>
    </>
  );
};

export default OverviewContent;
